import { Alert } from '@creditornot/cb-alert';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import { getAdCampaign } from 'apis/ad-bookings/api';
import { getCampaign } from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import { CampaignsQueryKeys } from '../campaignsQueryKeys';
import { CampaignPageLayout } from '../components/CampaignPageLayout';
import { AdCampaignDetails } from '../components/forms/AdCampaignDetails/AdCampaignDetails';
import BasketDiscountDetails from '../components/forms/BasketDiscountDetails/BasketDiscountDetails';
import DeliveryDiscountDetails from '../components/forms/DeliveryDiscountDetails/DeliveryDiscountDetails';
import EndOfDayCampaignDetails from '../components/forms/EndOfDayCampaignDetails/EndOfDayCampaignDetails';
import ItemDiscountDetails from '../components/forms/ItemDiscountDetails/ItemDiscountDetails';
import { useCampaignModuleContext } from '../context';
import { useCampaignConfiguration } from '../hooks/useCampaignConfiguration';
import { useCampaignMutations } from '../hooks/useCampaignMutations';
import useCampaignsOptions from '../hooks/useCampaignsOptions';
import { useCampaignsRoutes } from '../hooks/useCampaignsRoutes';
import { CampaignAction, CampaignsRoutesParams, CampaignStatus } from '../types';
import { getBasketDiscountType } from '../utils/getBasketDiscountType';
import { getCampaignActions } from '../utils/getCampaignActions';
import { getCampaignStatus } from '../utils/getCampaignStatus';
import { useTelemetryCampaignDetailsView } from '../utils/telemetry/telemetry';

export type DetailsCampaignPageProps = {
  merchantId: string;
  venueIds: string[];
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  country: Country;
  menuId?: string;
  campaignTypesPermittedToEdit: CampaignType[];
  timezone: string;
};

const StyledAlert = styled(Alert)`
  margin-block-start: 32px;
  margin-inline: 16px;
`;

export const DetailsCampaignPage = ({
  campaignTypesPermittedToEdit,
  merchantId,
  venueIds = [],
  venueDetails,
  menuId,
  timezone,
  country,
}: DetailsCampaignPageProps) => {
  const { params } = useRouteMatch<CampaignsRoutesParams>();
  const { entityType } = useCampaignModuleContext();
  const campaignsRoutes = useCampaignsRoutes();
  const { getCampaignTitleByType } = useCampaignsOptions();
  const history = useHistory();
  const { deleteCampaign, terminate } = useCampaignMutations({ merchantId, venueIds });
  const queryClient = useQueryClient();
  const [queryParams] = useQueryParams({
    campaignType: StringParam,
  });

  const isAdCampaign = queryParams.campaignType === CampaignType.ADS;
  const isAdCampaignsEditable = campaignTypesPermittedToEdit.includes(CampaignType.ADS);

  const { data: campaign, isLoading: isCampaignLoading } = useQuery(
    CampaignsQueryKeys.GET_SINGLE(merchantId, params.campaignId),
    () =>
      getCampaign(merchantId, params.campaignId).then(campaign => ({
        ...campaign,
        campaign_type: campaign.is_end_of_day_campaign ? CampaignType.END_OF_DAY : campaign.campaign_type,
      })),
    {
      refetchOnWindowFocus: false,
      enabled: !isAdCampaign && !!merchantId && !!params.campaignId,
      retry: false,
    },
  );

  const { data: adCampaign, isLoading: isAdCampaignLoading } = useQuery(
    CampaignsQueryKeys.GET_SINGLE_ADS(venueIds[0], params.campaignId),
    () =>
      getAdCampaign(venueIds[0], params.campaignId).then(campaign => ({
        ...campaign,
        campaign_type: CampaignType.ADS,
        status: campaign.status.toLowerCase() as CampaignStatus,
      })),
    {
      refetchOnWindowFocus: false,
      enabled: isAdCampaign && isAdCampaignsEditable && !!merchantId && !!params.campaignId,
      retry: false,
    },
  );

  const currentCampaign = campaign || adCampaign;

  const {
    configurationQuery: { isLoading: isConfigurationLoading },
    campaignTypesAvailableToEdit,
  } = useCampaignConfiguration({ merchantId, venueIds, campaignTypesPermittedToEdit });

  const isDataLoading = (isCampaignLoading && !isAdCampaign) || (isAdCampaign && isAdCampaignLoading);

  const isLoading =
    isCampaignLoading || isConfigurationLoading || deleteCampaign.isLoading || terminate.isLoading || isDataLoading;

  const campaignStatus = isAdCampaign ? adCampaign?.status : getCampaignStatus(campaign); // TODO: @adtech revert back after proper transormation

  const campaignVenueCount = isAdCampaign ? 1 : campaign?.venue_ids.length; // Ad Campaign only support single venue id.

  const handleClone = () => {
    history.push(`${campaignsRoutes.new}?campaignId=${campaign?.id}`);
  };

  const handleEdit = (campaignId: string) => history.push(campaignsRoutes.edit(campaignId));

  const handleDelete = (campaignId: string) =>
    deleteCampaign.mutate(
      { merchantId, campaignId },
      {
        onSuccess: () => history.push(campaignsRoutes.index),
      },
    );

  const handleTerminate = (campaignId: string) =>
    terminate.mutate(
      { merchantId, campaignId },
      {
        onSuccess: () => queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE(merchantId, params.campaignId)),
      },
    );

  useTelemetryCampaignDetailsView(currentCampaign?.campaign_type);

  const availableActions: CampaignAction[] | undefined =
    campaignStatus &&
    currentCampaign &&
    getCampaignActions({
      campaignType: currentCampaign.campaign_type,
      campaignStatus,
      country,
      isCreatedByWolt: 'created_by_wolt' in currentCampaign ? currentCampaign.created_by_wolt : undefined,
      isMerchantLevel: entityType === 'merchant',
      venueIds: 'venue_ids' in currentCampaign ? currentCampaign.venue_ids : venueIds,
    });

  if ((adCampaign && !isAdCampaignsEditable) || (campaign && !availableActions?.includes('read'))) {
    return <Redirect to={campaignsRoutes.index} />;
  }

  const bannerKeys = [];
  if (campaignStatus === CampaignStatus.RUNNING && country === 'ISR') {
    bannerKeys.push('campaigns.edit-end-unavailable');
  }
  if (campaign && campaign.venue_ids.length > 1 && entityType !== 'merchant') {
    bannerKeys.push('campaigns.error-multi-venue-campaign');
  }
  const banner =
    bannerKeys.length > 0 ? (
      <Alert variant="warning">
        {bannerKeys.map(key => (
          <div>
            <FormattedMessage key={key} id={key} />
          </div>
        ))}
      </Alert>
    ) : undefined;

  return (
    <CampaignPageLayout
      heading={getCampaignTitleByType(currentCampaign?.campaign_type)}
      isLoading={isDataLoading}
      status={campaignStatus}
      venueCount={campaignVenueCount}
    >
      {campaign && (
        <>
          {campaign.campaign_type === CampaignType.DELIVERY_DISCOUNT && (
            <DeliveryDiscountDetails
              venueDetails={venueDetails}
              banner={banner}
              campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
              campaign={campaign}
              country={country}
              timezone={timezone}
              isLoading={isLoading}
              availableActions={availableActions}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
              onTerminate={handleTerminate}
            />
          )}

          {campaign.campaign_type === CampaignType.BASKET_DISCOUNT && (
            <BasketDiscountDetails
              venueDetails={venueDetails}
              banner={banner}
              discountType={getBasketDiscountType(campaign.basket_discount)}
              campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
              campaign={campaign}
              country={country}
              timezone={timezone}
              isLoading={isLoading}
              availableActions={availableActions}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
              onTerminate={handleTerminate}
            />
          )}

          {campaign.campaign_type === CampaignType.ITEM_DISCOUNT && (
            <>
              {menuId ? (
                <ItemDiscountDetails
                  banner={banner}
                  campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
                  campaign={campaign}
                  country={country}
                  timezone={timezone}
                  isLoading={isLoading}
                  availableActions={availableActions}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onDelete={handleDelete}
                  onTerminate={handleTerminate}
                  menuId={menuId}
                />
              ) : (
                <StyledAlert variant="error">
                  <FormattedMessage id="campaigns.menu-unavailable" />
                </StyledAlert>
              )}
            </>
          )}

          {campaign.campaign_type === CampaignType.END_OF_DAY && (
            <>
              {menuId ? (
                <EndOfDayCampaignDetails
                  banner={banner}
                  campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
                  campaign={campaign}
                  country={country}
                  timezone={timezone}
                  isLoading={isLoading}
                  availableActions={availableActions}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onDelete={handleDelete}
                  onTerminate={handleTerminate}
                  menuId={menuId}
                />
              ) : (
                <StyledAlert variant="error">
                  <FormattedMessage id="campaigns.menu-unavailable" />
                </StyledAlert>
              )}
            </>
          )}
        </>
      )}
      {adCampaign && <AdCampaignDetails venueId={venueIds[0]} campaign={adCampaign} venueDetails={venueDetails} />}
    </CampaignPageLayout>
  );
};
